/**
 * Pure, framework-agnostic image operations -- the image-category
 * equivalent of pdf/operations.ts. Each function takes a File/canvas
 * and primitive options in, and returns a processed Blob/canvas out --
 * no React state, no toast calls, no analytics. Callable from a React
 * component, a future per-category "master tool" that combines several
 * operations, or an eventual server-side API built on the same logic.
 *
 * Re-exports the image utilities that already existed in utils.ts
 * (getImageOutputFormat, loadImageElement, compressImageToTarget,
 * withCorrectExtension) so this file becomes the single place to import
 * image-tool logic from going forward, without breaking the existing
 * components that still import directly from "./utils".
 */
export {
  getImageOutputFormat,
  loadImageElement,
  compressImageToTarget,
  compressImageAtQuality,
  withCorrectExtension,
  type ImageCompressionOutcome,
} from "./utils";

import { getImageOutputFormat, loadImageElement, withCorrectExtension } from "./utils";
import { decodeImage, flattenOnColor, canvasToBlob } from "./decode";
import { applyScanTone, type ToneInfo } from "./scannerUtils";
import { applyFilterToCanvas, type FilterSettings } from "./pixelFilters";

/**
 * Converts an image File to a different format/mimeType, extracted from
 * ConvertImage.tsx. Quality only applies to lossy formats (JPEG, WebP);
 * ignored for PNG, which is always lossless.
 */
/**
 * Converts an image between formats via canvas re-encode. When the
 * target format has no alpha channel (JPEG), the canvas is filled with
 * a solid background color first -- a real, confirmed bug otherwise:
 * an unfilled canvas defaults to transparent, which browsers typically
 * render as solid BLACK once re-encoded as JPEG (JPEG has no alpha to
 * preserve the transparency itself), not the white most people expect
 * for something like a logo. White is the sensible default since it's
 * what almost every JPEG viewing context (a white page, a white UI)
 * actually looks like, but the caller can pass a different fill color
 * when that's a better match for where the image will be used.
 */
export async function convertImage(
  file: File,
  mimeType: string,
  quality = 0.92,
  backgroundColor = "#ffffff"
): Promise<Blob> {
  const { canvas } = await decodeImage(file);
  const drawn = mimeType === "image/jpeg" ? flattenOnColor(canvas, backgroundColor) : canvas;
  return canvasToBlob(drawn, mimeType, quality);
}

/**
 * Converts an image to a clean, high-contrast "scanned" look, with
 * adjustable contrast. Grayscale is the default look (most documents
 * scan best that way), but kept optional rather than forced -- a
 * colored diagram, ID card, or highlighted receipt genuinely needs its
 * real colors preserved, and there was previously no way to keep them
 * once this became the only mode. Previously also offered a hard
 * black/white threshold mode, removed after feedback that the
 * threshold result looked poor in practice compared to grayscale.
 */
export function scanDocument(
  img: HTMLImageElement | HTMLCanvasElement,
  contrast: number,
  grayscale: boolean = true,
  report?: Partial<ToneInfo>
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = img instanceof HTMLCanvasElement ? img.width : img.naturalWidth;
  canvas.height = img instanceof HTMLCanvasElement ? img.height : img.naturalHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  // Contrast and grayscale live in scannerUtils.applyScanTone so a dark or flat page is never stretched to black (B-0174).
  const info = applyScanTone(data, contrast, grayscale);
  if (report) Object.assign(report, info);
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

export interface QuadCorners {
  topLeft: { x: number; y: number };
  topRight: { x: number; y: number };
  bottomRight: { x: number; y: number };
  bottomLeft: { x: number; y: number };
}

/**
 * Straightens a photo taken at an angle -- a document photographed
 * off-axis appears as a skewed quadrilateral rather than a clean
 * rectangle ("elongated" or keystoned). This corrects that given four
 * corner points marking where the document's actual corners are in the
 * source photo, warping that quadrilateral into a clean
 * outputWidth x outputHeight rectangle.
 *
 * This is a MANUAL correction -- the four corners have to be placed by
 * the person doing the scan, there's no automatic edge detection here.
 * Real automatic document-edge detection is a substantially larger
 * computer-vision project (reliable edge/contour detection against
 * arbitrary backgrounds is genuinely hard to get right), so this
 * offers the practical, honest alternative: point at where the actual
 * corners are, and get a mathematically correct straightening from
 * that -- the same fallback approach real scanner apps offer when
 * their own auto-detection doesn't find a good result.
 *
 * Implementation: computes a projective (homography) transform mapping
 * the unit square to the given quadrilateral -- the standard closed-form
 * method (see Paul Heckbert's 1989 thesis on projective mappings,
 * "Fundamentals of Texture Mapping and Image Warping") -- then, for
 * every pixel in the destination rectangle, maps it backward through
 * that same transform to find the corresponding source pixel
 * (inverse/backward mapping, which is what avoids gaps in the output --
 * forward-mapping source pixels into the destination can leave holes
 * where no source pixel happened to land). Each sampled point uses
 * bilinear interpolation between its four nearest source pixels rather
 * than picking the single nearest one, which meaningfully reduces
 * jagged/blocky artifacts in the straightened result.
 */
export function perspectiveCorrect(
  img: HTMLImageElement,
  corners: QuadCorners,
  outputWidth: number,
  outputHeight: number
): HTMLCanvasElement {
  const { topLeft, topRight, bottomRight, bottomLeft } = corners;

  // Coefficients for the unit-square -> quadrilateral homography.
  const dx1 = topRight.x - bottomRight.x;
  const dx2 = bottomLeft.x - bottomRight.x;
  const dx3 = topLeft.x - topRight.x + bottomRight.x - bottomLeft.x;
  const dy1 = topRight.y - bottomRight.y;
  const dy2 = bottomLeft.y - bottomRight.y;
  const dy3 = topLeft.y - topRight.y + bottomRight.y - bottomLeft.y;

  let a: number, b: number, c: number, d: number, e: number, f: number, g: number, h: number;
  const EPSILON = 1e-10;
  if (Math.abs(dx3) < EPSILON && Math.abs(dy3) < EPSILON) {
    // The four points already form a parallelogram -- a pure affine
    // map (no perspective term needed) handles this case exactly.
    a = topRight.x - topLeft.x;
    b = bottomLeft.x - topLeft.x;
    c = topLeft.x;
    d = topRight.y - topLeft.y;
    e = bottomLeft.y - topLeft.y;
    f = topLeft.y;
    g = 0;
    h = 0;
  } else {
    const denom = dx1 * dy2 - dx2 * dy1;
    g = (dx3 * dy2 - dx2 * dy3) / denom;
    h = (dx1 * dy3 - dx3 * dy1) / denom;
    a = topRight.x - topLeft.x + g * topRight.x;
    b = bottomLeft.x - topLeft.x + h * bottomLeft.x;
    c = topLeft.x;
    d = topRight.y - topLeft.y + g * topRight.y;
    e = bottomLeft.y - topLeft.y + h * bottomLeft.y;
    f = topLeft.y;
  }

  // Maps a unit-square point (u,v), both in [0,1], to its corresponding
  // point inside the source quadrilateral.
  const mapUnitSquareToQuad = (u: number, v: number): { x: number; y: number } => {
    const denom = g * u + h * v + 1;
    return { x: (a * u + b * v + c) / denom, y: (d * u + e * v + f) / denom };
  };

  const srcCanvas = document.createElement("canvas");
  srcCanvas.width = img.naturalWidth;
  srcCanvas.height = img.naturalHeight;
  const srcCtx = srcCanvas.getContext("2d")!;
  srcCtx.drawImage(img, 0, 0);
  const srcData = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height).data;

  const outCanvas = document.createElement("canvas");
  outCanvas.width = outputWidth;
  outCanvas.height = outputHeight;
  const outCtx = outCanvas.getContext("2d")!;
  const outImageData = outCtx.createImageData(outputWidth, outputHeight);
  const outData = outImageData.data;

  const sampleBilinear = (x: number, y: number): [number, number, number, number] => {
    const x0 = Math.floor(x), y0 = Math.floor(y);
    const x1 = Math.min(x0 + 1, srcCanvas.width - 1), y1 = Math.min(y0 + 1, srcCanvas.height - 1);
    const fx = x - x0, fy = y - y0;
    const idx = (px: number, py: number) => (py * srcCanvas.width + px) * 4;
    const out: [number, number, number, number] = [0, 0, 0, 0];
    for (let ch = 0; ch < 4; ch++) {
      const topRow = srcData[idx(x0, y0) + ch] * (1 - fx) + srcData[idx(x1, y0) + ch] * fx;
      const bottomRow = srcData[idx(x0, y1) + ch] * (1 - fx) + srcData[idx(x1, y1) + ch] * fx;
      out[ch] = topRow * (1 - fy) + bottomRow * fy;
    }
    return out;
  };

  for (let outY = 0; outY < outputHeight; outY++) {
    for (let outX = 0; outX < outputWidth; outX++) {
      const u = outX / outputWidth;
      const v = outY / outputHeight;
      const src = mapUnitSquareToQuad(u, v);
      const outIdx = (outY * outputWidth + outX) * 4;

      if (src.x < 0 || src.x >= srcCanvas.width - 1 || src.y < 0 || src.y >= srcCanvas.height - 1) {
        // Outside the source image entirely (can happen if a corner
        // was dragged past the photo's own edge) -- leave transparent
        // rather than sampling garbage or wrapping around.
        outData[outIdx + 3] = 0;
        continue;
      }
      const [r, gr, bl, al] = sampleBilinear(src.x, src.y);
      outData[outIdx] = r;
      outData[outIdx + 1] = gr;
      outData[outIdx + 2] = bl;
      outData[outIdx + 3] = al;
    }
  }

  outCtx.putImageData(outImageData, 0, 0);
  return outCanvas;
}

/**
 * Resizes an image File to specific pixel dimensions (stretching, not
 * cropping, if the aspect ratio doesn't match), extracted from
 * ResizeCrop.tsx. Output format matches the original file.
 */
export function drawResized(src: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement {
  // Reductions of more than 2x are done in halving steps: one big jump skips pixels and bands fine detail.
  let cur = src;
  while (cur.width / 2 >= width && cur.height / 2 >= height) {
    const step = document.createElement("canvas");
    step.width = Math.max(1, Math.floor(cur.width / 2));
    step.height = Math.max(1, Math.floor(cur.height / 2));
    const sctx = step.getContext("2d")!;
    sctx.imageSmoothingQuality = "high";
    sctx.drawImage(cur, 0, 0, step.width, step.height);
    cur = step;
  }
  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const octx = out.getContext("2d")!;
  octx.imageSmoothingQuality = "high";
  octx.drawImage(cur, 0, 0, width, height);
  return out;
}

export async function resizeImage(
  file: File,
  width: number,
  height: number
): Promise<{ blob: Blob; filename: string }> {
  const { canvas: src } = await decodeImage(file);
  return canvasToBlobMatchingFormat(drawResized(src, width, height), file);
}

/**
 * Crops an image File to a region specified as fractions (0-1) of the
 * original width/height, extracted from ResizeCrop.tsx -- fractions
 * rather than pixels because that's how the UI's drag-selection already
 * expresses a crop region, independent of the image's actual displayed
 * size on screen.
 */
export function cropRect(region: { x: number; y: number; w: number; h: number }, W: number, H: number) {
  // Round the EDGES, not the width/height: flooring both separately made 800.4 px come out as 799.
  const x0 = Math.max(0, Math.round(region.x * W));
  const y0 = Math.max(0, Math.round(region.y * H));
  const x1 = Math.min(W, Math.round((region.x + region.w) * W));
  const y1 = Math.min(H, Math.round((region.y + region.h) * H));
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
}

export async function cropImage(
  file: File,
  region: { x: number; y: number; w: number; h: number }
): Promise<{ blob: Blob; filename: string; width: number; height: number }> {
  const { canvas: src } = await decodeImage(file);
  const r = cropRect(region, src.width, src.height);
  const canvas = document.createElement("canvas");
  canvas.width = r.w;
  canvas.height = r.h;
  canvas.getContext("2d")!.drawImage(src, r.x, r.y, r.w, r.h, 0, 0, r.w, r.h);
  return { ...(await canvasToBlobMatchingFormat(canvas, file)), width: r.w, height: r.h };
}

export type FlipDirection = "horizontal" | "vertical";

/**
 * Flips an existing canvas in place (mutates and returns the same
 * canvas), extracted from FlipRotateImage.tsx. Canvas-in/canvas-out
 * rather than File-in/Blob-out, matching how the interactive tool
 * actually uses it -- multiple transforms can be chained on one canvas
 * before a final download, rather than round-tripping through a File
 * for every single step.
 */
export function flipImageCanvas(canvas: HTMLCanvasElement, direction: FlipDirection): HTMLCanvasElement {
  const ctx = canvas.getContext("2d")!;
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = canvas.width;
  tempCanvas.height = canvas.height;
  const tCtx = tempCanvas.getContext("2d")!;
  tCtx.translate(direction === "horizontal" ? canvas.width : 0, direction === "vertical" ? canvas.height : 0);
  tCtx.scale(direction === "horizontal" ? -1 : 1, direction === "vertical" ? -1 : 1);
  tCtx.drawImage(canvas, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(tempCanvas, 0, 0);
  return canvas;
}

/**
 * Rotates an existing canvas 90 degrees clockwise in place (mutates and
 * returns the same canvas, with swapped width/height), extracted from
 * FlipRotateImage.tsx.
 */
export function rotateImageCanvas90(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = canvas.getContext("2d")!;
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = canvas.height;
  tempCanvas.height = canvas.width;
  const tCtx = tempCanvas.getContext("2d")!;
  tCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
  tCtx.rotate(Math.PI / 2);
  tCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
  canvas.width = tempCanvas.width;
  canvas.height = tempCanvas.height;
  ctx.drawImage(tempCanvas, 0, 0);
  return canvas;
}

/**
 * Loads a File onto a fresh canvas, ready for flipImageCanvas/
 * rotateImageCanvas90 or any other canvas-based transform. Extracted
 * from FlipRotateImage.tsx's handleFiles.
 */
export async function loadImageAsCanvas(file: File): Promise<HTMLCanvasElement> {
  return (await decodeImage(file)).canvas;
}

/**
 * Downloads a canvas as a Blob, using the original file's format so a
 * flip/rotate/filter doesn't silently balloon a small JPEG into a much
 * larger PNG -- the same fix already applied by hand in
 * FlipRotateImage.tsx and ImageFilters.tsx, now shared in one place.
 */
export async function canvasToBlobMatchingFormat(
  canvas: HTMLCanvasElement,
  originalFile: File
): Promise<{ blob: Blob; filename: string }> {
  const { mimeType, extension } = getImageOutputFormat(originalFile);
  const quality = mimeType === "image/jpeg" ? 0.92 : undefined;
  const blob = await new Promise<Blob>(resolve =>
    canvas.toBlob(b => resolve(b!), mimeType, quality)
  );
  // If the browser could not write the original format it returns PNG: name the file honestly.
  const wroteOther = blob.type && blob.type !== mimeType;
  const filename = withCorrectExtension(originalFile.name, wroteOther ? "png" : extension);
  return { blob, filename };
}

/**
 * Applies a CSS filter string (brightness/contrast/grayscale/etc,
 * whatever a canvas 2D context's `filter` property accepts) to an
 * image File and returns the rendered result, extracted from
 * ImageFilters.tsx. Output format matches the original file, same as
 * canvasToBlobMatchingFormat.
 */
export async function applyImageFilter(
  file: File,
  settings: FilterSettings
): Promise<{ blob: Blob; filename: string }> {
  const { canvas } = await decodeImage(file);
  return canvasToBlobMatchingFormat(applyFilterToCanvas(canvas, settings), file);
}
